import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import date from '@gem/services/date-service';
import { getAlbumLayout, IImageSize } from '@gem/utils';

import { PayloadType } from 'APP/model/message/messageModel.types';
import { AlbumPayload } from 'STORE/Messages/Message/Payload/AlbumPayload/AlbumPayload';

import { TAlbumItemPayload } from '../MediaMessagePopup.types';

export interface IAlbumLayout {
  id: number;
  items: IImageSize[];
}

export interface IAlbumPayload {
  id: string;
  payload: TAlbumItemPayload;
}

export interface IMediaMessageAlbumPresenter {
  payload: AlbumPayload;
  payloads: IAlbumPayload[];
  canBeRemoved: boolean;
  layout: IAlbumLayout[];
  hasPlayIcon(id: number): boolean;
  getDuration(id: number): string;
  previewUrl(id: number): string;
  onRemove(id: number): void;
  setPayload(value: AlbumPayload): void;
}

export const useMediaMessageAlbumPresenter = (
  payload: AlbumPayload,
  onRemove?: (payload: TAlbumItemPayload) => void
): IMediaMessageAlbumPresenter => {
  const presenter = useLocalObservable<IMediaMessageAlbumPresenter>(() => ({
    payload: payload,

    get payloads(): IAlbumPayload[] {
      return presenter.payload.payloads;
    },

    get layout(): IAlbumLayout[] {
      const sizes = presenter.payloads.map((itemPayload: IAlbumPayload, idx: number) => ({
        w: itemPayload.payload.graphicSize?.width,
        h: itemPayload.payload.graphicSize?.height,
        id: idx,
      })) as IImageSize[];

      return getAlbumLayout(sizes).map((row, idx) => ({ id: idx, items: row }));
    },

    get canBeRemoved(): boolean {
      return Boolean(onRemove);
    },

    setPayload(value: AlbumPayload): void {
      presenter.payload = value;
    },

    hasPlayIcon(id: number): boolean {
      return presenter.payloads[id]?.payload?.payloadType === PayloadType.Video;
    },

    getDuration(id: number): string {
      return date.secondsToString(presenter.payloads[id]?.payload?.duration || 0);
    },

    previewUrl(id: number): string {
      const payload = presenter.payloads[id]?.payload;
      if (!payload) {
        return '';
      }

      return payload.localPreview || payload.previewUrl || '';
    },

    onRemove(id: number): void {
      if (!onRemove || !presenter.payloads[id]) {
        return;
      }

      onRemove(presenter.payloads[id].payload);
    },
  }));

  useEffect(() => {
    presenter.setPayload(payload);
  }, [payload]);

  return presenter;
};
